/**
 * ImportTemplateView
 *
 * "Import from JSON" view rendered inside the Templates gallery dialog
 * (same view-swap pattern as TemplatePreview). Accepts an AutoTemplate JSON
 * via file upload or paste, validates it against the AutoTemplate schema,
 * registers it in the imported-template store, and hands the resulting
 * template id back to the gallery so it can show the standard card preview.
 */

import { useCallback, useRef, useState } from 'react';
import { makeStyles, tokens, Text, Button } from '@coreai/fluentui-react';
import {
  ArrowLeft24Regular,
  ArrowUpload16Regular,
  Dismiss24Regular,
  ErrorCircle16Regular,
} from '@fluentui/react-icons';
import { validateAutoTemplate } from '@/types/autoTemplate';
import { useImportedTemplateStore } from '@/stores/importedTemplateStore';
import { telemetryService } from '@/services/telemetryService';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor/JsonEditor';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    flexShrink: 0,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
  },
  hint: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  fileName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  editorWrapper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    backgroundColor: tokens.colorPaletteRedBackground1,
    maxHeight: '120px',
    overflowY: 'auto',
    flexShrink: 0,
    ...thinScrollbarStyles,
  },
  errorTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  errorItem: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    fontFamily: tokens.fontFamilyMonospace,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    flexShrink: 0,
  },
  hiddenInput: {
    display: 'none',
  },
});

// =============================================================================
// PROPS
// =============================================================================

export interface ImportTemplateViewProps {
  onBack: () => void;
  onDismiss: () => void;
  /** Called with the registered template id after a successful import. */
  onImported: (templateId: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ImportTemplateView: React.FC<ImportTemplateViewProps> = ({
  onBack,
  onDismiss,
  onImported,
}) => {
  const styles = useStyles();
  const [jsonText, setJsonText] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTextChange = useCallback((value: string) => {
    setJsonText(value);
    setErrors([]);
  }, []);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-triggers change.
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      setJsonText(text);
      setFileName(file.name);
      setErrors([]);
    } catch {
      setErrors(['Could not read the selected file.']);
    }
  }, []);

  const handleImport = useCallback(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setErrors([`Not valid JSON: ${err instanceof Error ? err.message : String(err)}`]);
      return;
    }

    const result = validateAutoTemplate(parsed);
    if (!result.success) {
      setErrors(result.errors);
      telemetryService.trackEvent('template.import_failed', {
        errorCount: String(result.errors.length),
      });
      return;
    }

    const template = useImportedTemplateStore.getState().importTemplate(result.data);
    telemetryService.trackEvent('template.imported', { templateId: template.id });
    onImported(template.id);
  }, [jsonText, onImported]);

  const canImport = jsonText.trim().length > 0;

  return (
    <div className={styles.container} role="region" aria-label="Import template from JSON">
      {/* Header */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={onBack}
          aria-label="Back to gallery"
        />
        <Text as="h2" className={styles.headerTitle}>
          Import from JSON
        </Text>
        <Button
          appearance="subtle"
          icon={<Dismiss24Regular />}
          onClick={onDismiss}
          aria-label="Close template gallery"
        />
      </div>

      {/* Body */}
      <div className={styles.body}>
        <Text className={styles.hint}>
          Upload or paste an AutoTemplate <code>manifest.json</code> file — for example one
          generated with “Generate Template” from a succeeded run. The template is validated
          and shown as a template card preview so you can test it before contributing it.
        </Text>

        <div className={styles.toolbar}>
          <Button icon={<ArrowUpload16Regular />} onClick={handleBrowseClick}>
            Upload file
          </Button>
          {fileName && <Text className={styles.fileName}>{fileName}</Text>}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className={styles.hiddenInput}
            onChange={handleFileChange}
            aria-label="Choose a template JSON file"
            tabIndex={-1}
          />
        </div>

        <div className={styles.editorWrapper}>
          <JsonEditor
            value={jsonText}
            onChange={handleTextChange}
            fill
            hasError={errors.length > 0}
            placeholder="Paste template JSON here"
          />
        </div>

        {errors.length > 0 && (
          <div className={styles.errorBox} role="alert">
            <Text className={styles.errorTitle}>
              <ErrorCircle16Regular aria-hidden="true" />
              This file is not a valid template
            </Text>
            {errors.map((err, i) => (
              <Text key={i} className={styles.errorItem}>
                {err}
              </Text>
            ))}
          </div>
        )}

        <div className={styles.actions}>
          <Button appearance="primary" onClick={handleImport} disabled={!canImport}>
            Import
          </Button>
          <Button appearance="secondary" onClick={onBack}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

ImportTemplateView.displayName = 'ImportTemplateView';
